import type { FileData } from "../types";

type FormatEntry = { url?: string; mime?: string; ext?: string };
type FormatsMap = Record<string, FormatEntry>;
type ImageFormat = "large" | "medium" | "small" | "thumbnail" | "xs";

const FORMAT_FALLBACK_ORDER: ImageFormat[] = [
  "large",
  "medium",
  "small",
  "thumbnail",
  "xs",
];

let webpSupportCache: boolean | null = null;

function supportsWebp(): boolean {
  if (webpSupportCache !== null) return webpSupportCache;

  if (typeof document === "undefined") {
    webpSupportCache = true;
    return webpSupportCache;
  }

  try {
    const canvas = document.createElement("canvas");
    webpSupportCache =
      canvas.toDataURL("image/webp").indexOf("data:image/webp") === 0;
  } catch {
    webpSupportCache = false;
  }

  return webpSupportCache;
}

function isWebpUrl(url: string): boolean {
  const lower = url.toLowerCase();
  return lower.includes(".webp") || lower.includes("format=webp");
}

function isWebpFormat(format: FormatEntry | undefined): boolean {
  if (!format) return false;
  if (format.mime?.toLowerCase() === "image/webp") return true;
  if (format.ext?.toLowerCase() === ".webp") return true;
  return typeof format.url === "string" ? isWebpUrl(format.url) : false;
}

function isUsableFormat(
  format: FormatEntry | undefined,
  webpSupported: boolean,
): format is FormatEntry & { url: string } {
  if (!format?.url) return false;
  if (!webpSupported && isWebpFormat(format)) return false;
  return true;
}

export const getImageUrl = (
  image: FileData | null | undefined,
  preferredFormat: ImageFormat = "small",
): string | null => {
  if (!image) return null;
  const webpSupported = supportsWebp();

  if (
    image.formats &&
    typeof image.formats === "object" &&
    !Array.isArray(image.formats)
  ) {
    const formats = image.formats as FormatsMap;
    const preferred = formats[preferredFormat];

    if (isUsableFormat(preferred, webpSupported)) {
      return preferred.url;
    }

    const orderedFormats: ImageFormat[] = [
      preferredFormat,
      ...FORMAT_FALLBACK_ORDER.filter((f) => f !== preferredFormat),
    ];

    for (const formatKey of orderedFormats) {
      const candidate = formats[formatKey];
      if (isUsableFormat(candidate, webpSupported)) {
        return candidate.url;
      }
    }
  }

  if (typeof image.url !== "string" || image.url.length === 0) {
    return null;
  }

  if (!webpSupported && isWebpUrl(image.url)) {
    return null;
  }

  return image.url;
};
